import React from 'react';
import { BBCodeNode } from './bbcode.types';
import {
  ContentParagraph,
  ContentHeading,
  ContentBold,
  ContentItalic,
  ContentImage,
  ContentList,
  ContentListItem,
  ContentSpacer,
} from '../content/GameContent.styles';

interface BBCodeRendererProps {
  ast: BBCodeNode & { type: 'document' };
  resolveImage: (src: string) => string;
}

export const BBCodeRenderer: React.FC<BBCodeRendererProps> = ({ ast, resolveImage }) => {
  return <>{ast.children.map((node, index) => renderNode(node, `node-${index}`, resolveImage))}</>;
};

function renderNode(
  node: BBCodeNode,
  key: string,
  resolveImage: (src: string) => string,
): React.ReactNode {
  switch (node.type) {
    case 'text':
      return node.content;

    case 'paragraph': {
      if (!node.children || node.children.length === 0) {
        return <ContentSpacer key={key} />;
      }

      return (
        <ContentParagraph key={key} $align={node.align}>
          {node.children.map((child, i) => renderNode(child, `${key}-${i}`, resolveImage))}
        </ContentParagraph>
      );
    }

    case 'heading':
      return (
        <ContentHeading key={key} $align={node.align}>
          {node.children.map((child, i) => renderNode(child, `${key}-${i}`, resolveImage))}
        </ContentHeading>
      );

    case 'bold':
      return (
        <ContentBold key={key}>
          {node.children.map((child, i) => renderNode(child, `${key}-${i}`, resolveImage))}
        </ContentBold>
      );

    case 'italic':
      return (
        <ContentItalic key={key}>
          {node.children.map((child, i) => renderNode(child, `${key}-${i}`, resolveImage))}
        </ContentItalic>
      );

    case 'image': {
      const resolvedSrc = resolveImage(node.src);
      if (!resolvedSrc) {
        return null;
      }
      return <ContentImage key={key} src={resolvedSrc} alt="" loading="lazy" />;
    }

    case 'list':
      return (
        <ContentList key={key}>
          {node.children.map((child, i) => renderNode(child, `${key}-${i}`, resolveImage))}
        </ContentList>
      );

    case 'listItem':
      return (
        <ContentListItem key={key}>
          {node.children.map((child, i) => renderNode(child, `${key}-${i}`, resolveImage))}
        </ContentListItem>
      );

    case 'document':
      return (
        <React.Fragment key={key}>
          {node.children.map((child, i) => renderNode(child, `doc-${i}`, resolveImage))}
        </React.Fragment>
      );

    default: {
      const _exhaustiveCheck: never = node;
      return _exhaustiveCheck;
    }
  }
}
